import { Fragment, useMemo, useState, useEffect, useRef } from 'react';
import Select, { SingleValue } from 'react-select';
import { useIntl } from 'react-intl';
import { KeenIcon } from '@/components';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { usePermissions } from '@/hooks/usePermissions';
import { useSnackbar } from 'notistack';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import {
  RoutingGraphDiagnosticsResponse,
  RoutingGraphDiagnosticsPayload,
  useRunRoutingGraphDiagnosticsMutation,
} from '@/services/RoutingManagementService';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapboxAccessToken } from '@/hooks/useMapboxAccessToken';
import NumberInput from '@/components/inputControls/NumberInput';

type HarborOption = {
  value: string;
  label: string;
  coordinates: [number, number];
};

type FormState = {
  originHarborId: string;
  destinationHarborId: string;
  minDepth: string;
  minHeight: string;
  allowUnknownHeight: boolean;
  originCoords: [number, number] | null;
  destinationCoords: [number, number] | null;
};

const initialFormState: FormState = {
  originHarborId: '',
  destinationHarborId: '',
  minDepth: '',
  minHeight: '',
  allowUnknownHeight: true,
  originCoords: null,
  destinationCoords: null,
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const maybeError = error as {
    data?: {
      message?: string;
      data?: {
        message?: string;
      };
    };
    message?: string;
  };

  return (
    maybeError?.data?.message ||
    maybeError?.data?.data?.message ||
    maybeError?.message ||
    fallback
  );
};

// const JsonSection = ({ title, value }: { title: string; value: unknown }) => {
//   if (
//     value === null ||
//     value === undefined ||
//     (typeof value === 'object' &&
//       !Array.isArray(value) &&
//       Object.keys(value as Record<string, unknown>).length === 0)
//   ) {
//     return null;
//   }

//   return (
//     <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
//       <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
//       <pre className="mt-3 overflow-x-auto rounded-lg bg-gray-50 p-4 text-xs leading-6 text-gray-700">
//         {JSON.stringify(value, null, 2)}
//       </pre>
//     </div>
//   );
// };

interface DiagnosticPath {
  frontier?: [number, number];
  distance_to_goal_m?: number;
  path_geojson?: GeoJSON.FeatureCollection;
}

interface DiagnosticReachability {
  from_start?: DiagnosticPath;
  from_destination?: DiagnosticPath;
}

interface DiagnosticGap {
  midpoint?: [number, number];
  distance_m?: number;
  line_geojson?: GeoJSON.FeatureCollection;
  bbox?: number[];
}

interface DiagnosticSnapPoint {
  input?: [number, number];
  snapped?: [number, number];
  distanceMeters?: number;
}

interface DiagnosticSnapped {
  source?: DiagnosticSnapPoint;
  destination?: DiagnosticSnapPoint;
}

interface DiagnosticMetadata {
  graph_version?: string;
  duration_ms?: number;
}

interface DiagnosticSegment {
  grid?: string;
  depth?: number;
  height?: number;
  is_manual?: boolean;
  annotation_name?: string;
  filename?: string;
}

const RoutingDebugger = () => {
  const { currentLayout } = useLayout();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [webglError, setWebglError] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [result, setResult] = useState<RoutingGraphDiagnosticsResponse | null>(
    null
  );
  const [showRawJson, setShowRawJson] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);

  const reachability = result?.reachability as unknown as
    | DiagnosticReachability
    | undefined;
  const gap = result?.gap as unknown as DiagnosticGap | undefined;
  const snapped = result?.snapped as unknown as DiagnosticSnapped | undefined;

  const { isMapboxReady } = useMapboxAccessToken();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const toNumber = (value: number | string | null | undefined) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  };

  const [harborSearch, setHarborSearch] = useState('');
  const [debouncedHarborSearch, setDebouncedHarborSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHarborSearch(harborSearch.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [harborSearch]);

  const [selectedOrigin, setSelectedOrigin] = useState<HarborOption | null>(
    null
  );
  const [selectedDestination, setSelectedDestination] =
    useState<HarborOption | null>(null);

  const { data: harborsData, isLoading: isHarborsLoading } = useGetHarborsQuery(
    {
      page: 1,
      limit: 100,
      search: debouncedHarborSearch || undefined,
    }
  );

  const [runDiagnostics, { isLoading: isRunning }] =
    useRunRoutingGraphDiagnosticsMutation();

  const harborOptions = useMemo<HarborOption[]>(() => {
    const harbors = harborsData?.data || [];

    const list = harbors
      .map((harbor) => {
        const lat = toNumber(harbor.latitude);
        const lng = toNumber(harbor.longitude);
        return {
          ...harbor,
          lat,
          lng,
        };
      })
      .filter((harbor) => harbor.lng !== undefined && harbor.lat !== undefined)
      .map((harbor) => ({
        value: harbor.id,
        label: harbor.name,
        coordinates: [harbor.lng!, harbor.lat!] as [number, number],
      }));

    if (selectedOrigin && !list.some((o) => o.value === selectedOrigin.value)) {
      list.push(selectedOrigin);
    }
    if (
      selectedDestination &&
      !list.some((o) => o.value === selectedDestination.value)
    ) {
      list.push(selectedDestination);
    }

    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [harborsData, selectedOrigin, selectedDestination]);

  const originOption = selectedOrigin;
  const destinationOption = selectedDestination;

  useEffect(() => {
    if (!isMapboxReady || !mapContainerRef.current) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      return;
    }

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    if (!mapboxgl.supported()) {
      setWebglError(true);
      return;
    }

    let center: [number, number] = [24.9384, 60.1699];

    const getFirstCoord = (geojsonObj: unknown): [number, number] | null => {
      if (!geojsonObj) return null;
      const geojson = geojsonObj as GeoJSON.FeatureCollection;
      if (geojson && geojson.features && geojson.features.length > 0) {
        const firstFeature = geojson.features[0];
        const geometry = firstFeature.geometry;
        if (geometry && 'coordinates' in geometry) {
          const coords = (
            geometry as GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon
          ).coordinates;
          if (Array.isArray(coords) && coords.length > 0) {
            if (Array.isArray(coords[0]) && coords[0].length >= 2) {
              const firstCoord = coords[0] as [number, number];
              return [firstCoord[0], firstCoord[1]];
            } else if (coords.length >= 2 && typeof coords[0] === 'number') {
              return [coords[0] as number, coords[1] as number];
            }
          }
        }
      }
      return null;
    };

    const routeCoord = getFirstCoord(result?.route_geojson);
    const startCoord = getFirstCoord(reachability?.from_start?.path_geojson);
    const destCoord = getFirstCoord(
      reachability?.from_destination?.path_geojson
    );
    const gapCoord = getFirstCoord(gap?.line_geojson);

    const firstValidCoord =
      routeCoord ||
      startCoord ||
      destCoord ||
      gapCoord ||
      formState.originCoords ||
      formState.destinationCoords;

    if (firstValidCoord) {
      center = firstValidCoord;
    }

    try {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/hmesikam/cmhkf5ut7001v01sgdjrdhn2b',
        center,
        zoom: 7,
      });

      mapRef.current = map;

      map.on('load', () => {
        if (!mapRef.current) return;

        const bounds = new mapboxgl.LngLatBounds();
        let hasCoordinates = false;

        const extendBoundsFromFeatureCollection = (geojsonObj: unknown) => {
          if (!geojsonObj) return;
          const fc = geojsonObj as GeoJSON.FeatureCollection;
          if (!fc.features) return;
          fc.features.forEach((feature) => {
            const geometry = feature.geometry;
            if (geometry && 'coordinates' in geometry) {
              const coords = (
                geometry as GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon
              ).coordinates;
              if (Array.isArray(coords)) {
                const addCoords = (arr: unknown[]) => {
                  if (typeof arr[0] === 'number') {
                    bounds.extend(arr as unknown as [number, number]);
                    hasCoordinates = true;
                  } else {
                    arr.forEach((item) => {
                      if (Array.isArray(item)) {
                        addCoords(item as unknown[]);
                      }
                    });
                  }
                };
                addCoords(coords);
              }
            }
          });
        };

        // 1. Add Route line if exists
        if (result?.route_geojson) {
          const geojson =
            result.route_geojson as unknown as GeoJSON.FeatureCollection;
          mapRef.current.addSource('route', {
            type: 'geojson',
            data: geojson,
          });
          mapRef.current.addLayer({
            id: 'route-line',
            type: 'line',
            source: 'route',
            paint: {
              'line-color': '#007cbf',
              'line-width': 4,
            },
          });
          extendBoundsFromFeatureCollection(geojson);
        }

        // 2. Add start reachability line if exists
        if (reachability?.from_start?.path_geojson) {
          const geojson = reachability.from_start.path_geojson;
          mapRef.current.addSource('reachability-start', {
            type: 'geojson',
            data: geojson,
          });
          mapRef.current.addLayer({
            id: 'reachability-start-line',
            type: 'line',
            source: 'reachability-start',
            paint: {
              'line-color': '#10b981',
              'line-width': 4,
            },
          });
          extendBoundsFromFeatureCollection(geojson);
        }

        // 3. Add destination reachability line if exists
        if (reachability?.from_destination?.path_geojson) {
          const geojson = reachability.from_destination.path_geojson;
          mapRef.current.addSource('reachability-dest', {
            type: 'geojson',
            data: geojson,
          });
          mapRef.current.addLayer({
            id: 'reachability-dest-line',
            type: 'line',
            source: 'reachability-dest',
            paint: {
              'line-color': '#f59e0b',
              'line-width': 4,
            },
          });
          extendBoundsFromFeatureCollection(geojson);
        }

        // 4. Add gap line if exists
        if (gap?.line_geojson) {
          const geojson = gap.line_geojson;
          mapRef.current.addSource('gap', {
            type: 'geojson',
            data: geojson,
          });
          mapRef.current.addLayer({
            id: 'gap-line-layer',
            type: 'line',
            source: 'gap',
            paint: {
              'line-color': '#ef4444',
              'line-width': 3,
              'line-dasharray': [2, 2],
            },
          });
          extendBoundsFromFeatureCollection(geojson);
        }

        // Draw snap lines if snapped points exist in result
        if (snapped?.source?.input && snapped?.source?.snapped) {
          mapRef.current.addSource('snap-line-source', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: [snapped.source.input, snapped.source.snapped],
              },
            } as GeoJSON.Feature,
          });
          mapRef.current.addLayer({
            id: 'snap-line-layer',
            type: 'line',
            source: 'snap-line-source',
            paint: {
              'line-color': '#95a5a6',
              'line-width': 2,
              'line-dasharray': [2, 2],
            },
          });

          new mapboxgl.Marker({ color: '#e67e22', scale: 0.8 })
            .setLngLat(snapped.source.snapped)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div>
                  <h4 style="font-weight: bold; color: #e67e22;">Snapped Origin</h4>
                  <p style="margin: 4px 0 0 0; font-size: 11px;">Distance: ${snapped.source.distanceMeters?.toFixed(1) || '0'} m</p>
                </div>`
              )
            )
            .addTo(map);

          bounds.extend(snapped.source.snapped);
          hasCoordinates = true;
        }

        if (snapped?.destination?.input && snapped?.destination?.snapped) {
          mapRef.current.addSource('snap-line-dest-source', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: [
                  snapped.destination.input,
                  snapped.destination.snapped,
                ],
              },
            } as GeoJSON.Feature,
          });
          mapRef.current.addLayer({
            id: 'snap-line-dest-layer',
            type: 'line',
            source: 'snap-line-dest-source',
            paint: {
              'line-color': '#95a5a6',
              'line-width': 2,
              'line-dasharray': [2, 2],
            },
          });

          new mapboxgl.Marker({ color: '#e67e22', scale: 0.8 })
            .setLngLat(snapped.destination.snapped)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div>
                  <h4 style="font-weight: bold; color: #e67e22;">Snapped Destination</h4>
                  <p style="margin: 4px 0 0 0; font-size: 11px;">Distance: ${snapped.destination.distanceMeters?.toFixed(1) || '0'} m</p>
                </div>`
              )
            )
            .addTo(map);

          bounds.extend(snapped.destination.snapped);
          hasCoordinates = true;
        }

        if (formState.originCoords) {
          bounds.extend(formState.originCoords);
          hasCoordinates = true;
          new mapboxgl.Marker({ color: '#22c55e' })
            .setLngLat(formState.originCoords)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(`<h3>Origin</h3>`)
            )
            .addTo(map);
        }
        if (formState.destinationCoords) {
          bounds.extend(formState.destinationCoords);
          hasCoordinates = true;
          new mapboxgl.Marker({ color: '#ef4444' })
            .setLngLat(formState.destinationCoords)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(`<h3>Destination</h3>`)
            )
            .addTo(map);
        }

        // Place a gap midpoint marker if there is a gap
        if (
          gap?.midpoint &&
          Array.isArray(gap.midpoint) &&
          gap.midpoint.length >= 2
        ) {
          const gapMid = gap.midpoint;
          const distStr = gap.distance_m
            ? `${(Number(gap.distance_m) / 1000).toFixed(2)} km`
            : 'Unknown';
          const reasonStr = result?.failure_reason || 'Blocked';
          new mapboxgl.Marker({ color: '#7c3aed' })
            .setLngLat(gapMid)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div>
                  <h4 style="font-weight: bold; color: #7c3aed;">Gap Detected</h4>
                  <p style="margin: 4px 0 0 0; font-size: 11px;">Distance: ${distStr}</p>
                  <p style="margin: 2px 0 0 0; font-size: 11px;">Reason: ${reasonStr}</p>
                </div>`
              )
            )
            .addTo(map);
        }

        if (hasCoordinates) {
          try {
            map.fitBounds(bounds, { padding: 40, maxZoom: 14 });
          } catch (err) {
            console.error('Error fitting bounds:', err);
          }
        }
      });
    } catch (e) {
      console.error('WebGL/Mapbox Error:', e);
      setWebglError(true);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [
    result,
    isMapboxReady,
    formState.originCoords,
    formState.destinationCoords,
  ]);

  const handleHarborChange =
    (field: 'originHarborId' | 'destinationHarborId') =>
    (option: SingleValue<HarborOption>) => {
      if (field === 'originHarborId') {
        setSelectedOrigin(option);
        setFormState((prev) => ({
          ...prev,
          originHarborId: option?.value || '',
          originCoords: option ? option.coordinates : null,
        }));
      } else {
        setSelectedDestination(option);
        setFormState((prev) => ({
          ...prev,
          destinationHarborId: option?.value || '',
          destinationCoords: option ? option.coordinates : null,
        }));
      }
    };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleReset = () => {
    setFormState(initialFormState);
    setSelectedOrigin(null);
    setSelectedDestination(null);
    setResult(null);
  };

  const executeDiagnostics = async () => {
    if (!formState.originCoords || !formState.destinationCoords) {
      enqueueSnackbar(
        'Please set both origin and destination (select harbors or click on map).',
        {
          variant: 'warning',
        }
      );
      return;
    }

    if (formState.minDepth.trim() === '' || formState.minHeight.trim() === '') {
      enqueueSnackbar('Please enter both minimum depth and minimum height.', {
        variant: 'warning',
      });
      return;
    }

    const minDepth = Number(formState.minDepth);
    const minHeight = Number(formState.minHeight);

    if (Number.isNaN(minDepth) || Number.isNaN(minHeight)) {
      enqueueSnackbar('Depth and height must be valid numbers.', {
        variant: 'warning',
      });
      return;
    }

    try {
      const response = await runDiagnostics({
        origin: formState.originCoords,
        destination: formState.destinationCoords,
        min_depth: minDepth,
        min_height: minHeight,
        allow_unknown_height: formState.allowUnknownHeight,
        include_reachability_geometry: true,
      } as RoutingGraphDiagnosticsPayload).unwrap();

      setResult(response);
      setIsPanelExpanded(true);
      enqueueSnackbar('Routing diagnostics completed.', {
        variant: 'success',
      });
    } catch (error) {
      setResult(null);
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to run routing diagnostics.'),
        {
          variant: 'error',
        }
      );
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await executeDiagnostics();
  };

  const warnings = (result?.metadata?.warnings || []) as string[];
  const sameSnap = !!(
    snapped?.source?.snapped &&
    snapped?.destination?.snapped &&
    JSON.stringify(snapped.source.snapped) ===
      JSON.stringify(snapped.destination.snapped)
  );
  const probes = (result?.classification?.probes || []) as Array<{
    name: string;
    detail: string;
    passed: boolean;
  }>;
  if (!hasPermission('routing_management_read')) {
    return null;
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle />
              <ToolbarDescription>
                Run internal routing graph diagnostics for harbor-to-harbor
                connectivity and constraint failures.
              </ToolbarDescription>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div className="space-y-6">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900 dark:border-amber-400 dark:text-amber-200 contrast:bg-amber-900 contrast:border-amber-400 contrast:text-amber-200 p-4 text-sm text-amber-900">
            This tool is intended for internal debugging only. Diagnostics may
            take time and can be expensive on the backend.
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-gray-200 bg-gray-100 p-6 shadow-sm"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-2">
                <label
                  htmlFor="routing-debugger-origin"
                  className="block text-sm font-medium text-gray-700"
                >
                  Origin harbor
                </label>
                <Select
                  inputId="routing-debugger-origin"
                  options={harborOptions}
                  value={originOption}
                  onChange={handleHarborChange('originHarborId')}
                  onInputChange={(newValue) => setHarborSearch(newValue)}
                  onMenuOpen={() => setHarborSearch('')}
                  placeholder={
                    isHarborsLoading ? 'Loading harbors...' : 'Select origin'
                  }
                  isDisabled={isRunning}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                  isSearchable
                />
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">
                    {formState.originCoords
                      ? `Coords: [${formState.originCoords.map((c) => c.toFixed(5)).join(', ')}]`
                      : 'Coords: not set'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="routing-debugger-destination"
                  className="block text-sm font-medium text-gray-700"
                >
                  Destination harbor
                </label>
                <Select
                  inputId="routing-debugger-destination"
                  options={harborOptions}
                  value={destinationOption}
                  onChange={handleHarborChange('destinationHarborId')}
                  onInputChange={(newValue) => setHarborSearch(newValue)}
                  onMenuOpen={() => setHarborSearch('')}
                  placeholder={
                    isHarborsLoading
                      ? 'Loading harbors...'
                      : 'Select destination'
                  }
                  isDisabled={isRunning}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                  isSearchable
                />
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">
                    {formState.destinationCoords
                      ? `Coords: [${formState.destinationCoords.map((c) => c.toFixed(5)).join(', ')}]`
                      : 'Coords: not set'}
                  </span>
                </div>
              </div>

              <div>
                <label
                  htmlFor="minDepth"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Minimum depth (m)
                </label>
                <NumberInput
                  id="minDepth"
                  name="minDepth"
                  step="0.1234567890"
                  min="0"
                  value={formState.minDepth}
                  onChange={handleInputChange}
                  disabled={isRunning}
                  className="w-full"
                  placeholder="Enter minimum depth"
                />
              </div>

              <div>
                <label
                  htmlFor="minHeight"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Minimum height (m)
                </label>
                <NumberInput
                  id="minHeight"
                  name="minHeight"
                  step="0.1234567890"
                  min="0"
                  value={formState.minHeight}
                  onChange={handleInputChange}
                  disabled={isRunning}
                  className="w-full"
                  placeholder="Enter minimum height"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row justify-between items-center w-full">
              <div className="flex flex-row gap-3 ">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isRunning}
                >
                  {isRunning ? 'Running diagnostics...' : 'Run diagnostics'}
                </button>
                <button
                  type="button"
                  className="btn btn-light border border-gray-300"
                  onClick={handleReset}
                  disabled={isRunning}
                >
                  Reset
                </button>
              </div>
              {/* <button
                type="button"
                className="btn btn-light border border-gray-300 text-xs py-2 px-3"
                onClick={loadStorkorsPreset}
                disabled={isRunning}
              >
                Storkors regression preset
              </button> */}
            </div>
          </form>

          {/* Interactive Map Visualizer */}
          <div className="rounded-2xl border border-gray-200 bg-gray-100 p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              {result
                ? result.connected
                  ? 'Route Visualization'
                  : 'Diagnostics Map (Disconnected)'
                : 'Diagnostics Map'}
            </h2>
            {webglError ? (
              <div className="w-full min-h-[400px] flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
                <div className="max-w-md text-center">
                  <div className="flex justify-center mb-4">
                    <KeenIcon
                      icon="shield-cross"
                      className="text-4xl text-danger"
                    />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {intl.formatMessage({ id: 'COMMON.WEBGL_ERROR_TITLE' })}
                  </h3>
                  <p className="text-gray-600 mb-6 font-medium">
                    {intl.formatMessage({
                      id: 'COMMON.WEBGL_ERROR_MESSAGE',
                    })}
                  </p>
                  <div className="bg-gray-200 p-5 rounded-xl border border-gray-200 text-left shadow-sm">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">
                      {intl.formatMessage({
                        id: 'COMMON.WEBGL_HOW_TO_ENABLE',
                      })}
                    </p>
                    <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {intl.formatMessage({
                        id: 'COMMON.WEBGL_ERROR_INSTRUCTIONS',
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative w-full h-[600px] rounded-lg overflow-hidden border border-gray-200">
                <div
                  ref={mapContainerRef}
                  className="w-full h-full"
                  role="region"
                  aria-label="Map displaying route"
                />

                {/* Mobile Open Button */}
                {!isPanelExpanded && (
                  <button
                    type="button"
                    onClick={() => setIsPanelExpanded(true)}
                    className="absolute top-5 right-5 z-20 md:hidden flex items-center gap-1.5 py-2 px-3 bg-gray-100 text-gray-800 font-semibold rounded-lg shadow-md border border-gray-200 text-xs transition-colors hover:bg-gray-200"
                  >
                    <KeenIcon
                      icon="setting-2"
                      className="text-sm text-gray-600"
                    />
                    Diagnostics
                  </button>
                )}

                {/* Floating Diagnostics Panel */}
                <div
                  className={`absolute top-5 right-5 w-[calc(100%-2.5rem)] sm:w-[380px] max-h-[calc(100%-2.5rem)] bg-gray-100 rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.15)] z-10 overflow-y-auto p-4 flex flex-col gap-4 border border-gray-100 transition-all duration-300 ${
                    isPanelExpanded
                      ? 'translate-x-0 opacity-100'
                      : 'translate-x-[120%] md:translate-x-0 opacity-0 md:opacity-100 pointer-events-none md:pointer-events-auto'
                  }`}
                >
                  {/* Close button for Mobile inside the panel header */}
                  <div className="flex justify-between items-center md:hidden border-b border-gray-100 pb-2">
                    <span className="font-bold text-sm text-gray-800">
                      Diagnostics Panel
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsPanelExpanded(false)}
                      className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <KeenIcon icon="cross" className="text-lg" />
                    </button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {!result && (
                      <button
                        type="button"
                        onClick={executeDiagnostics}
                        className="w-full py-2.5 px-4 bg-[#1e8449] hover:bg-[#196f3d] contrast:bg-green-800 contrast:hover:bg-green-700 text-white font-medium rounded text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                        disabled={isRunning}
                      >
                        {isRunning
                          ? 'Running diagnostics…'
                          : 'Run graph diagnostics'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleReset}
                      className="w-full py-2.5 px-4 bg-[#c0392b] hover:bg-[#a93226] contrast:bg-red-800 contrast:hover:bg-red-700 text-white font-medium rounded text-sm transition-colors"
                    >
                      Clear overlays
                    </button>
                  </div>

                  {result && (
                    <div className="flex flex-col gap-4 border-t border-gray-100 pt-4">
                      <div className="text-[15px] font-bold text-gray-800">
                        Results
                      </div>

                      {/* Summary Card */}
                      <div
                        className={`p-3 text-xs leading-relaxed rounded-md border-l-4 bg-gray-200 ${
                          result.connected
                            ? 'border-[#27ae60] text-gray-700'
                            : 'border-[#e74c3c] text-gray-700'
                        }`}
                      >
                        <strong className="text-sm block mb-1">
                          {result.connected ? 'Connected' : 'Not connected'}
                        </strong>
                        <div>Status: {result.status || '—'}</div>
                        <div>Failure: {result.failure_reason || '—'}</div>
                        <div>
                          Graph version:{' '}
                          {(result.metadata as DiagnosticMetadata)
                            ?.graph_version || '—'}
                        </div>
                        <div>
                          Duration:{' '}
                          {(result.metadata as DiagnosticMetadata)
                            ?.duration_ms ?? '—'}{' '}
                          ms
                        </div>

                        {/* Same snap warning inside card */}
                        {sameSnap && (
                          <div className="mt-2 text-red-600 font-bold">
                            Both points snapped to the same vertex — not a real
                            A→B route.
                          </div>
                        )}

                        {/* Warnings inside summary card */}
                        {warnings.length > 0 && (
                          <div className="mt-3 pt-2 border-t border-gray-200">
                            <strong className="block text-gray-900 mb-1">
                              Warnings
                            </strong>
                            <div className="space-y-1 text-red-700">
                              {warnings.map((w, idx) => (
                                <div key={idx} className="leading-snug">
                                  • {w}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Probes List */}
                      {probes.length > 0 && (
                        <div className="flex flex-col gap-1.5">
                          {probes.map((probe, idx) => (
                            <div
                              key={idx}
                              className={`p-2.5 rounded text-xs font-medium ${
                                probe.passed
                                  ? 'bg-green-100 dark:bg-green-900 contrast:bg-green-900  text-green-800 dark:text-green-200 contrast:text-green-200'
                                  : 'bg-red-100 dark:bg-red-900 contrast:bg-red-900 text-red-800 dark:text-red-200 contrast:text-red-200'
                              }`}
                            >
                              <strong>{probe.name}</strong>: {probe.detail}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Snapped Info */}
                      <div className="text-xs text-gray-700 space-y-1 leading-normal">
                        <strong className="block text-gray-800 font-bold mb-1">
                          Snapped Points Info
                        </strong>
                        <div>
                          <strong>Snapped Origin:</strong>{' '}
                          {snapped?.source?.snapped
                            ? JSON.stringify(snapped.source.snapped)
                            : 'None'}{' '}
                          {snapped?.source?.distanceMeters !== undefined &&
                            `(${snapped.source.distanceMeters.toFixed(1)} m)`}
                        </div>
                        <div>
                          <strong>Snapped Destination:</strong>{' '}
                          {snapped?.destination?.snapped
                            ? JSON.stringify(snapped.destination.snapped)
                            : 'None'}{' '}
                          {snapped?.destination?.distanceMeters !== undefined &&
                            `(${snapped.destination.distanceMeters.toFixed(1)} m)`}
                        </div>
                      </div>

                      {/* Segments near gap */}
                      <div className="flex flex-col border-t border-gray-100 pt-3">
                        <div className="text-[14px] font-bold text-gray-800 mb-2">
                          Segments near gap
                        </div>
                        <div className="max-h-[180px] overflow-y-auto space-y-1.5 pr-1">
                          {result.segments && result.segments.length > 0 ? (
                            (
                              result.segments as unknown as DiagnosticSegment[]
                            ).map((s, idx) => (
                              <div
                                key={idx}
                                className="text-xs p-2 bg-gray-100 rounded text-gray-700 leading-normal border border-gray-100"
                              >
                                <span className="font-semibold text-gray-800">
                                  Grid:
                                </span>{' '}
                                {s.grid || '—'} |{' '}
                                <span className="font-semibold text-gray-800">
                                  Depth:
                                </span>{' '}
                                {s.depth ?? '—'} |{' '}
                                <span className="font-semibold text-gray-800">
                                  Height:
                                </span>{' '}
                                {s.height ?? '—'}
                                {s.is_manual && (
                                  <span className="inline-block bg-[#f39c12] text-white text-[9px] px-1.5 py-0.5 rounded ml-1 font-bold uppercase">
                                    manual
                                  </span>
                                )}
                                <br />
                                <span className="text-[11px] text-gray-500">
                                  {s.annotation_name || s.filename || ''}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="text-xs p-2 bg-gray-100 rounded text-gray-500 italic text-center border border-gray-100">
                              No segments near gap
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Raw JSON */}
                      <div className="flex flex-col pt-2">
                        <button
                          type="button"
                          onClick={() => setShowRawJson(!showRawJson)}
                          className="w-full py-2 px-3 bg-[#3498db] hover:bg-[#2980b9] text-white font-medium rounded text-xs transition-colors"
                        >
                          Toggle raw JSON
                        </button>
                        {showRawJson && (
                          <pre className="mt-2 max-h-[200px] overflow-auto bg-[#2c3e50] text-[#ecf0f1] text-[11px] p-2 rounded whitespace-pre-wrap break-all leading-normal font-mono">
                            {JSON.stringify(result, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </Fragment>
  );
};

export default RoutingDebugger;
